<template>
  <RcDialog :model-value="true" :title="$t('company_select_title')" :width="mobile ? '100%' : DialogUtils.MEDIUM"
    :fullscreen="mobile" :hideFooter="true" @closeDialog="handleClose">
    <div>
      <p class="text-medium-emphasis mb-4">
        {{ $t('people_company_change_body') }}
      </p>

      <CompanyChoiceList :rows="choices" :busyId="busyId" :message="pickMessage"
        @select="selectCompany" @clearMessage="pickMessage = ''" />
    </div>
  </RcDialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';

import RcDialog from '@/v3/components/library/dialog/RcDialog.vue';
import DialogUtils from '@/v3/utils/DialogUtils.js';
import { t } from '@/i18n/index.js';
import CompanyChoices from '@/v3/pages/dmz/CompanyChoices.js';
import CompanyChoiceList from '@/v3/pages/dmz/CompanyChoiceList.vue';

const emit = defineEmits(['closeDialog']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { mobile } = useDisplay();

const domain = computed(() => store.getters.domain);

// Every company where the person has a portal role, in every state (86e06umb3).
const choices = computed(() => (domain.value ? CompanyChoices.rowsFromStore(store) : []));
const busyId = ref(null);
const pickMessage = ref('');

const selectCompany = async (row) => {
  if (!domain.value || busyId.value) return;
  pickMessage.value = '';
  busyId.value = row.id;
  let result;
  try {
    result = await CompanyChoices.choose(store, row, {
      lockedFallback: t('company_select_locked_fallback'),
      failedMessage: t('company_select_enter_failed'),
    });
  } finally {
    busyId.value = null;
  }
  if (result.outcome !== CompanyChoices.Outcomes.ENTERED) {
    // Locked, suspended, or not let in: stay in the dialog, on the current company, and say why.
    pickMessage.value = result.message;
    return;
  }

  const company = domain.value.companys().findById(row.id);
  if (company.isNew()) {
    pickMessage.value = t('company_select_enter_failed');
    return;
  }

  const user = domain.value.session().user();
  const employee = user.employees().findByCompanyId(company.id());
  domain.value.session().with(company);
  domain.value.session().with(employee);
  domain.value.session().companyName = company.name();

  store.dispatch('AuthStorage_updateCompany', company);

  try {
    const cookieString = window.localStorage["readyChek"];
    if (cookieString) {
      const cookie = JSON.parse(cookieString);
      cookie['Company'] = company.id();
      window.localStorage["readyChek"] = JSON.stringify(cookie);
    }
  } catch (e) {
    console.error("Failed to update company in localStorage:", e);
  }

  // Stay on the page the user was on, under the new company. Every operator page reads
  // the company from :operatorId, so the URL must change before the reload: a push that
  // has not finished when reload() runs reloads the old URL, and the page then looks up
  // the new company's records under the old company's id. The query is dropped because it
  // can carry the old company's record ids.
  const target = route.params.operatorId
    ? { name: route.name, params: { ...route.params, operatorId: company.idEncoded() } }
    : { path: `/portal/v3/operator/${company.idEncoded()}/dashboard` };
  router.replace(target)
    .catch(err => console.error("Navigation error during company change:", err))
    .finally(() => window.location.reload());
};

const handleClose = () => {
  emit('closeDialog');
};
</script>
